import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminNavigation from './AdminNavigation';
import './AdminDashboard.css';
import { API_URL } from '../utils/config';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalProducts: 0,
    totalOrders: 0,
    totalUsers: 0,
    totalRevenue: 0,
    pendingOrders: 0,
    processingOrders: 0,
    shippedOrders: 0,
    deliveredOrders: 0,
    lowStockProducts: 0,
    newUsers: 0,
    todayOrders: 0,
    todayRevenue: 0
  });
  const [recentOrders, setRecentOrders] = useState([]);
  const [recentUsers, setRecentUsers] = useState([]);
  const [adminInfo, setAdminInfo] = useState(null);

  useEffect(() => {
    const adminToken = localStorage.getItem('adminToken');
    if (!adminToken) {
      navigate('/admin/login');
      return;
    }
    
    // Get admin info from localStorage
    const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
    setAdminInfo(adminUser);
    
    fetchDashboardData();
  }, [navigate]);

  const fetchDashboardData = async () => {
    try {
      const adminToken = localStorage.getItem('adminToken');
      const config = {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      };

      // Fetch all data in parallel
      const [productsRes, ordersRes, usersRes] = await Promise.all([
        axios.get(`${API_URL}/api/products`),
        axios.get(`${API_URL}/api/admin/orders`, config),
        axios.get(`${API_URL}/api/admin/users`, config)
      ]);

      const products = productsRes.data;
      const orders = ordersRes.data;
      const users = usersRes.data;

      // Calculate statistics
      const totalRevenue = orders
        .filter(o => o.status !== 'cancelled')
        .reduce((sum, o) => sum + (o.totalAmount || 0), 0);

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const todayOrders = orders.filter(o => 
        new Date(o.createdAt) >= today
      );

      const todayRevenue = todayOrders
        .filter(o => o.status !== 'cancelled')
        .reduce((sum, o) => sum + (o.totalAmount || 0), 0);

      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);

      const newUsers = users.filter(u => 
        new Date(u.createdAt) > weekAgo
      ).length;

      const lowStockProducts = products.filter(p => 
        (p.stock || 0) < 10
      ).length;

      setStats({
        totalProducts: products.length,
        totalOrders: orders.length,
        totalUsers: users.length,
        totalRevenue,
        pendingOrders: orders.filter(o => o.status === 'pending').length,
        processingOrders: orders.filter(o => o.status === 'processing').length,
        shippedOrders: orders.filter(o => o.status === 'shipped').length,
        deliveredOrders: orders.filter(o => o.status === 'delivered').length,
        lowStockProducts,
        newUsers,
        todayOrders: todayOrders.length,
        todayRevenue
      });

      // Get recent orders (last 5)
      setRecentOrders(orders.slice(0, 5));

      // Get recent users (last 5)
      setRecentUsers(users.slice(0, 5));

      setLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return amount.toLocaleString('en-BD') + ' ৳';
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 18) return 'Good Afternoon';
    return 'Good Evening';
  };

  if (loading) {
    return (
      <>
        <AdminNavigation />
        <div className="dashboard-container">
          <div className="loading-dashboard">
            <div className="spinner-large"></div>
            <p>Loading dashboard...</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <AdminNavigation />
      <div className="dashboard-container">
        {/* Welcome Header */}
        <div className="dashboard-welcome">
          <div className="welcome-text">
            <h1>{getGreeting()}, {adminInfo?.fullName || 'Admin'}! 👋</h1>
            <p>Here's what's happening with your store today</p>
          </div>
          <div className="welcome-date">
            <div className="date-card">
              <span className="date-icon">📅</span>
              <div>
                <p className="date-day">{new Date().toLocaleDateString('en-US', { weekday: 'long' })}</p>
                <p className="date-full">{new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Today's Stats */}
        <div className="today-stats">
          <div className="today-stat-card primary">
            <div className="stat-icon-circle">📦</div>
            <div className="stat-details">
              <h3>{stats.todayOrders}</h3>
              <p>Orders Today</p>
            </div>
          </div>
          <div className="today-stat-card success">
            <div className="stat-icon-circle">💰</div>
            <div className="stat-details">
              <h3>{formatCurrency(stats.todayRevenue)}</h3>
              <p>Revenue Today</p>
            </div>
          </div>
          <div className="today-stat-card warning">
            <div className="stat-icon-circle">⚠️</div>
            <div className="stat-details">
              <h3>{stats.pendingOrders}</h3>
              <p>Pending Orders</p>
            </div>
          </div>
          <div className="today-stat-card info">
            <div className="stat-icon-circle">👥</div>
            <div className="stat-details">
              <h3>{stats.newUsers}</h3>
              <p>New Users (7 days)</p>
            </div>
          </div>
        </div>

        {/* Main Statistics Grid */}
        <div className="stats-section">
          <h2 className="section-title">📊 Overview Statistics</h2>
          <div className="stats-grid">
            <div className="stat-card" onClick={() => navigate('/admin/products')}>
              <div className="stat-header">
                <div className="stat-icon products">📦</div>
                <span className="stat-trend up">+12%</span>
              </div>
              <div className="stat-body">
                <h3>{stats.totalProducts}</h3>
                <p>Total Products</p>
              </div>
              <div className="stat-footer">
                <span className="stat-detail">
                  {stats.lowStockProducts > 0 && (
                    <span className="warning-text">⚠️ {stats.lowStockProducts} low stock</span>
                  )}
                </span>
                <span className="stat-link">View All →</span>
              </div>
            </div>

            <div className="stat-card" onClick={() => navigate('/admin/orders')}>
              <div className="stat-header">
                <div className="stat-icon orders">🛒</div>
                <span className="stat-trend up">+8%</span>
              </div>
              <div className="stat-body">
                <h3>{stats.totalOrders}</h3>
                <p>Total Orders</p>
              </div>
              <div className="stat-footer">
                <span className="stat-detail">{stats.pendingOrders} pending</span>
                <span className="stat-link">Manage →</span>
              </div>
            </div>

            <div className="stat-card" onClick={() => navigate('/admin/users')}>
              <div className="stat-header">
                <div className="stat-icon users">👥</div>
                <span className="stat-trend up">+15%</span>
              </div>
              <div className="stat-body">
                <h3>{stats.totalUsers}</h3>
                <p>Total Users</p>
              </div>
              <div className="stat-footer">
                <span className="stat-detail">{stats.newUsers} new this week</span>
                <span className="stat-link">View All →</span>
              </div>
            </div>

            <div className="stat-card revenue">
              <div className="stat-header">
                <div className="stat-icon revenue-icon">💰</div>
                <span className="stat-trend up">+23%</span>
              </div>
              <div className="stat-body">
                <h3>{formatCurrency(stats.totalRevenue)}</h3>
                <p>Total Revenue</p>
              </div>
              <div className="stat-footer">
                <span className="stat-detail">All time earnings</span>
                <span className="stat-link">Details →</span>
              </div>
            </div>
          </div>
        </div>

        {/* Order Status Overview */}
        <div className="order-status-section">
          <h2 className="section-title">📋 Order Status Breakdown</h2>
          <div className="order-status-grid">
            <div className="status-card pending">
              <div className="status-icon">⏳</div>
              <h3>{stats.pendingOrders}</h3>
              <p>Pending</p>
              <div className="status-bar">
                <div className="status-progress" style={{ width: `${(stats.pendingOrders / stats.totalOrders) * 100}%` }}></div>
              </div>
            </div>

            <div className="status-card processing">
              <div className="status-icon">⚙️</div>
              <h3>{stats.processingOrders}</h3>
              <p>Processing</p>
              <div className="status-bar">
                <div className="status-progress" style={{ width: `${(stats.processingOrders / stats.totalOrders) * 100}%` }}></div>
              </div>
            </div>

            <div className="status-card shipped">
              <div className="status-icon">🚚</div>
              <h3>{stats.shippedOrders}</h3>
              <p>Shipped</p>
              <div className="status-bar">
                <div className="status-progress" style={{ width: `${(stats.shippedOrders / stats.totalOrders) * 100}%` }}></div>
              </div>
            </div>

            <div className="status-card delivered">
              <div className="status-icon">✅</div>
              <h3>{stats.deliveredOrders}</h3>
              <p>Delivered</p>
              <div className="status-bar">
                <div className="status-progress" style={{ width: `${(stats.deliveredOrders / stats.totalOrders) * 100}%` }}></div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="quick-actions-section">
          <h2 className="section-title">⚡ Quick Actions</h2>
          <div className="quick-actions-grid">
            <div className="action-card" onClick={() => navigate('/admin/products')}>
              <div className="action-icon">➕</div>
              <h3>Add Product</h3>
              <p>Add new product to store</p>
            </div>

            <div className="action-card" onClick={() => navigate('/admin/orders')}>
              <div className="action-icon">📦</div>
              <h3>Process Orders</h3>
              <p>Update order status</p>
            </div>

            <div className="action-card" onClick={() => navigate('/admin/users')}>
              <div className="action-icon">👥</div>
              <h3>Manage Users</h3>
              <p>View and edit users</p>
            </div>

            <div className="action-card" onClick={() => navigate('/admin/products')}>
              <div className="action-icon">📊</div>
              <h3>View Reports</h3>
              <p>Sales and analytics</p>
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="recent-activity-section">
          <div className="recent-orders">
            <div className="section-header">
              <h2 className="section-title">🛒 Recent Orders</h2>
              <button className="view-all-btn" onClick={() => navigate('/admin/orders')}>
                View All →
              </button>
            </div>
            <div className="activity-list">
              {recentOrders.length === 0 ? (
                <div className="no-activity">
                  <p>No recent orders</p>
                </div>
              ) : (
                recentOrders.map(order => (
                  <div key={order._id} className="activity-item" onClick={() => navigate('/admin/orders')}>
                    <div className="activity-icon order-icon">🛒</div>
                    <div className="activity-details">
                      <h4>Order #{order.orderNumber}</h4>
                      <p>{order.customer?.name || 'Customer'} • {formatCurrency(order.totalAmount)}</p>
                    </div>
                    <div className="activity-meta">
                      <span className={`status-badge ${order.status}`}>{order.status}</span>
                      <span className="activity-time">{formatDate(order.createdAt)}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="recent-users">
            <div className="section-header">
              <h2 className="section-title">👥 Recent Users</h2>
              <button className="view-all-btn" onClick={() => navigate('/admin/users')}>
                View All →
              </button>
            </div>
            <div className="activity-list">
              {recentUsers.length === 0 ? (
                <div className="no-activity">
                  <p>No recent users</p>
                </div>
              ) : (
                recentUsers.map(user => (
                  <div key={user._id} className="activity-item" onClick={() => navigate('/admin/users')}>
                    <div className="activity-icon user-icon">
                      {user.name?.charAt(0).toUpperCase() || '?'}
                    </div>
                    <div className="activity-details">
                      <h4>{user.name}</h4>
                      <p>{user.email}</p>
                    </div>
                    <div className="activity-meta">
                      <span className={`role-badge ${user.isAdmin ? 'admin' : 'user'}`}>
                        {user.isAdmin ? 'Admin' : 'User'}
                      </span>
                      <span className="activity-time">{formatDate(user.createdAt)}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminDashboard;